import { inject, Injectable, signal } from '@angular/core';
import {
  DataModel,
  DataModelConverter,
  PropertyType,
  RestService,
} from '@infodata-group/inf-commons';
import { DataEventEnum } from '@monorepo-projects/alwayson-shared';
import { mergeMap, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AlarmFactory } from '../../../factory/alarm.factory';
import { BbxFactory } from '../../../factory/bbx.factory';
import { FleetDriverFactory } from '../factory/fleet-driver.factory';
import { FleetFactory } from '../factory/new-fleet.factory';
import { VehicleExpenseFactory } from '../factory/vehicle-expense.factory';
import { VehicleMaintenanceFactory } from '../factory/vehicle-maintenance.factory';
import { VehicleFactory } from '../factory/vehicle.factory';
import { Fleet } from '../model/new-fleet';

@Injectable({
  providedIn: 'root',
})
export class FleetService {
  private restService = inject(RestService);

  protected emitter$ = new Subject<DataEventEnum>();

  protected fleet: Fleet;
  public isLoading = true;
  protected delay = 500;

  public fleetModel: DataModel;
  public bbxModel: DataModel;
  public presentationModel: DataModel;
  public nullSpeedModel: DataModel;
  public enginModel: DataModel;
  public fleetDriverModel: DataModel;
  public vehicleEditableModel: DataModel;
  public vehicleModel: DataModel;
  public maintenanceModel: DataModel;
  public expenseModel: DataModel;
  public alarmModel: DataModel;

  public bbxList = signal<{ id: string; lib: string }[]>([]);

  public initModels() {
    this.loadFleetDriverModel$('model')
      .pipe(
        mergeMap(() => this.loadVehicleModel$('model')),
        mergeMap(() => this.loadMaintenanceModel$('model')),
        mergeMap(() => this.loadExpenseModel$('model')),
        mergeMap(() => this.loadAlarmModel$()),
        mergeMap(() => this.loadBbxModel$()),
      )
      .subscribe({
        next: () => {
          console.log('All models have been loaded successfully');
          this.isLoading = false;
        },
        error: (err) => {
          console.error('An error occurred while loading models:', err);
        },
      });
  }

  loadFleetModel$() {
    return this.restService.options(new FleetFactory().getApi()).pipe(
      tap((model) => {
        this.fleetModel = model;
        const newModels = this.splitDataModelByPropertyIds(
          model,
          ['label', 'icon', 'id', 'group'],
          ['isSpeedNull', 'speedNullTime', 'speedNullKmh'],
          ['isEngin'],
        );
        this.presentationModel = newModels.presentation;
        this.nullSpeedModel = newModels.nullSpeed;
        this.enginModel = newModels.engin;
      }),
    );
  }

  splitDataModelByPropertyIds(
    model: DataModel,
    presentationModel: string[],
    nullSpeedModel: string[],
    enginModel: string[],
  ) {
    const presentationProperties = model.properties.filter((prop) =>
      presentationModel.includes(prop.id),
    );

    const nullSpeedProperties = model.properties.filter((prop) =>
      nullSpeedModel.includes(prop.id),
    );

    const enginProperties = model.properties.filter((prop) =>
      enginModel.includes(prop.id),
    );

    const presentation = new DataModel();
    presentation.properties = presentationProperties.map((prop) => {
      if (prop.id === 'id') {
        prop.displayId = false;
        prop.editType = PropertyType.combo;
        prop.src = 'ws';
        prop.ws = 'lite/bbx/non-attribuees/';
        prop.wsLib = 'lib';
      }
      return prop;
    });

    const nullSpeed = new DataModel();
    nullSpeed.id = 'nullSpeed';
    nullSpeed.properties = nullSpeedProperties;

    const engin = new DataModel();
    engin.id = 'engin';
    engin.properties = enginProperties;

    return { presentation, nullSpeed, engin };
  }

  loadBbxModel$() {
    return this.restService.options(new BbxFactory().getApi()).pipe(
      tap((model) => {
        this.bbxModel = model;
      }),
    );
  }

  loadFleetDriverModel$(fleetId: string) {
    return this.restService
      .options(new FleetDriverFactory(fleetId).getApi())
      .pipe(
        tap((model) => {
          model.properties.find((p) => p.id === 'date').default_value =
            new Date().toLocaleDateString('fr-FR');
          this.fleetDriverModel = model;
          this.fleetDriverModel.properties =
            this.fleetDriverModel.properties.map((p) => {
              p.disableOnUpdate = p.id === 'id';
              return p;
            });
        }),
      );
  }

  loadVehicleModel$(fleetId: string) {
    return this.restService.options(new VehicleFactory(fleetId).getApi()).pipe(
      tap((model) => {
        model.properties.find((p) => p.id === 'date').default_value =
          new Date().toLocaleDateString('fr-FR');
        this.vehicleModel = model;
        this.vehicleEditableModel = new DataModelConverter().convert(
          JSON.parse(JSON.stringify(model)),
        );
        this.vehicleEditableModel.properties =
          this.vehicleEditableModel.properties.map((p) => {
            p.disableOnUpdate = p.id === 'id';
            return p;
          });
      }),
    );
  }

  loadMaintenanceModel$(vehicleId: string) {
    return this.restService
      .options(new VehicleMaintenanceFactory(vehicleId).getApi())
      .pipe(
        tap((model) => {
          this.maintenanceModel = model;
        }),
      );
  }

  loadExpenseModel$(vehicleId: string) {
    return this.restService
      .options(new VehicleExpenseFactory(vehicleId).getApi())
      .pipe(
        tap((model) => {
          model.properties.find((p) => p.id === 'date').default_value =
            new Date().toLocaleDateString('fr-FR');
          this.expenseModel = model;
        }),
      );
  }

  loadAlarmModel$() {
    return this.restService.options(new AlarmFactory().getApi()).pipe(
      tap((model) => {
        model.properties.find((p) => p.id === 'id').existOnTable = false;
        model.properties.find((p) => p.id === 'bbx').existOnTable = false;
        model.properties.find((p) => p.id === 'driver').existOnTable = false;
        this.alarmModel = model;
      }),
    );
  }
}
